import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Spin } from "antd";
import { observer } from "mobx-react-lite";

import { getAutopayment, getPaymentMethod } from "../api/payment";
import { SubscriptionDialog } from "../components/subscription/SubscriptionDialog";
import {
  SubscriptionInfo,
  TeamPlanAlert,
} from "../components/subscription/SubscriptionInfo";
import { TrialBanner } from "../components/subscription/TrialBanner";
import { userStore } from "../stores/userStore";
import { usePageTitle } from "../utils/usePageTitle";

import "./SubscriptionPage.css";

import type { DialogMode } from "../components/subscription/SubscriptionDialog";

const STATUS_INTERVAL = 1000;
const PM_STATUS_INTERVAL = 3000;
const TIMEOUT_SHORT = 20 * 1000;
const TIMEOUT_LONG = 45 * 1000;

const isTerminalStatus = (s: string | null) =>
  ["canceled", "active", "failed", null].includes(s);

export const SubscriptionPage = observer(function SubscriptionPage() {
  const { t } = useTranslation();
  usePageTitle(t("subscription.title"));
  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [polling, setPolling] = useState(false);
  const [pollingPM, setPollingPM] = useState(false);

  const [urlParams] = useState(() => {
    const url = new URL(window.location.href);
    return {
      paymentMethodChanged: url.searchParams.get("payment-method-changed"),
      paymentStatus: url.searchParams.get("payment-attempt-status"),
      scenario: url.searchParams.get("scenario"),
    };
  });

  useEffect(() => {
    const { scenario } = urlParams;

    const url = new URL(window.location.href);
    url.searchParams.delete("payment-method-changed");
    url.searchParams.delete("payment-attempt-status");
    url.searchParams.delete("scenario");
    url.searchParams.delete("subscription-canceled");
    window.history.replaceState({}, "", url.toString());

    if (scenario === "invoiceRequested") {
      setDialogMode("invoice_requested");
    } else if (scenario === "cancellation") {
      setDialogMode("cancel");
    }
  }, [urlParams]);

  useEffect(() => {
    const { paymentMethodChanged } = urlParams;

    if (paymentMethodChanged) {
      setPollingPM(true);

      let resolvedPM = false;
      let cancelledPM = false;

      const resolvePM = () => {
        if (resolvedPM || cancelledPM) return;
        resolvedPM = true;
        setPollingPM(false);
        clearInterval(interval);
        userStore.reloadSubscription();
      };

      const checkPM = async () => {
        try {
          const data = await getPaymentMethod();
          if (cancelledPM) return;
          if (data) {
            if (data.status === "success" || data.status === "failed") {
              resolvePM();
            }
          } else {
            resolvePM();
          }
        } catch {
          if (cancelledPM) return;
          resolvePM();
        }
      };

      checkPM();
      const interval = setInterval(checkPM, PM_STATUS_INTERVAL);
      const pmTimeout = setTimeout(resolvePM, TIMEOUT_LONG);

      return () => {
        cancelledPM = true;
        clearInterval(interval);
        clearTimeout(pmTimeout);
      };
    }
  }, [urlParams]);

  useEffect(() => {
    const { paymentStatus, scenario } = urlParams;

    if (paymentStatus === "success" || paymentStatus === "unknown") {
      const timeout =
        paymentStatus === "success" ? TIMEOUT_LONG : TIMEOUT_SHORT;
      setPolling(true);

      let latestStatus: string | null = null;
      let latestHasError = false;
      let resolved = false;
      let cancelled = false;

      const resolve = () => {
        if (resolved || cancelled) return;
        resolved = true;
        setPolling(false);
        clearInterval(interval);

        if (latestStatus === "active" && !latestHasError) {
          userStore.reloadSubscription();
        }

        if (scenario === "creation") {
          if (latestStatus === "active" && !latestHasError) {
            setDialogMode("created");
          } else {
            setDialogMode("creation_failed");
          }
        } else if (scenario === "updating") {
          if (latestStatus === "active" && !latestHasError) {
            setDialogMode("updated");
          } else if (latestStatus !== null) {
            setDialogMode("update_failed");
          }
        }
      };

      const check = async () => {
        try {
          const data = await getAutopayment();
          if (cancelled) return;
          if (data) {
            latestStatus = data.status;
            latestHasError = !!data.latest_error;
            if (isTerminalStatus(data.status)) resolve();
          } else {
            resolve();
          }
        } catch {
          if (cancelled) return;
          resolve();
        }
      };

      check();
      const interval = setInterval(check, STATUS_INTERVAL);
      const timer = setTimeout(resolve, timeout);

      return () => {
        cancelled = true;
        clearInterval(interval);
        clearTimeout(timer);
      };
    }
  }, [urlParams]);

  const sub = userStore.subscription;
  const expireAt = sub?.expire_at ?? null;

  return (
    <div className="page subscription-page">
      <h1>{t("subscription.title")}</h1>
      {polling && (
        <div className="subscription-page__polling">
          <Spin size="large" />
          <p>{t("subscription.waitingResponseFromPaymentSystem")}</p>
        </div>
      )}
      {!polling && <TrialBanner goal="trial_main" />}
      {!polling && <SubscriptionInfo pollingPaymentMethod={pollingPM} />}
      {!polling && <TeamPlanAlert />}
      <SubscriptionDialog
        mode={dialogMode}
        expireAt={expireAt}
        onClose={() => setDialogMode(null)}
      />
    </div>
  );
});
